/**
 * Mobile Calculator Handler Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles calculator check button logic
 */

import { useCallback, RefObject } from "react";
import { formatAmountToCurrencyDecimalPlaces } from "../../../../utils/currencyDefaults";
import { TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface FeeEditFormData {
  feeAmount: string;
  feeAccountId: string;
}

export interface UseMobileCalculatorHandlerProps {
  transactionType: "expense" | "income" | "transfer";
  focusedField: FocusedField;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setFocusedField: React.Dispatch<React.SetStateAction<FocusedField>>;
  setCalculatorExpression: React.Dispatch<React.SetStateAction<string>>;
  setRateManuallyEdited: React.Dispatch<React.SetStateAction<boolean>>;
  setDefaultCurrencyRateManuallyEdited: React.Dispatch<React.SetStateAction<boolean>>;
  hasEvaluatedRef: RefObject<boolean>;
  handleFieldFocus: (field: FocusedField) => void;
  currencies: Array<{ code: string; decimalPlaces?: number }>;
  defaultCurrency: string;
  accounts: Array<{ id: string; currency: string }>;
  /** When true and focusedField === "feeAmount", updates feeEditFormData instead of transferFormData */
  isFeeEditMode?: boolean;
  feeEditFormData?: FeeEditFormData;
  setFeeEditFormData?: React.Dispatch<React.SetStateAction<FeeEditFormData>>;
}

export function useMobileCalculatorHandler({
  transactionType,
  focusedField,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  setFocusedField,
  setCalculatorExpression,
  setRateManuallyEdited,
  setDefaultCurrencyRateManuallyEdited,
  hasEvaluatedRef,
  handleFieldFocus,
  currencies,
  defaultCurrency,
  accounts,
  isFeeEditMode = false,
  feeEditFormData,
  setFeeEditFormData,
}: UseMobileCalculatorHandlerProps) {
  const handleCalculatorCheck = useCallback((value: string) => {
    // For amount, feeAmount, and measurable detail fields, keep "0" as "0" (don't convert to empty string)
    // For rate fields, convert "0" to empty string (as before)
    const isAmountOrFeeOrDetail = focusedField === "amount" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"));
    const cleanValue = (isAmountOrFeeOrDetail && value === "0") ? "0" : (value === "0" ? "" : value);

    // Mark that we've evaluated and will save
    hasEvaluatedRef.current = true;

    // Update calculator display to show the result
    setCalculatorExpression(value);

    // Save to form based on focused field
    if (typeof focusedField === "string" && focusedField.startsWith("detail:")) {
      const detailName = focusedField.slice(7);
      const toStore = cleanValue === "" || cleanValue === "0" ? "" : (parseFloat(cleanValue) || 0);
      setFormData((prev) => ({ ...prev, details: { ...(prev.details || {}), [detailName]: toStore } }));
      setFocusedField(null);
    } else if (focusedField === "rate") {
      setFormData((prev) => ({ ...prev, rate: cleanValue }));
      // Only mark as manually edited if the value actually changed (so refresh button stays disabled when user didn't change and value matches setting)
      const prevRate = (formData.rate ?? "").trim();
      const newRate = (cleanValue ?? "").trim();
      const bothEmptyOrZero = (prevRate === "" || prevRate === "0") && (newRate === "" || newRate === "0");
      const sameNumeric = prevRate !== "" && prevRate !== "0" && newRate !== "" && newRate !== "0" && Math.abs(parseFloat(prevRate) - parseFloat(newRate)) < 1e-9;
      // If user cleared the rate (empty string), mark as manually edited so auto-populate doesn't restore it
      // But refresh button will still work to restore the calculated rate
      if (newRate === "" && prevRate !== "") {
        setRateManuallyEdited(true);
      } else if (!bothEmptyOrZero && !sameNumeric) {
        setRateManuallyEdited(true);
      }
      // Close calculator immediately so rate field doesn't stay "selected" when category opens
      setFocusedField(null);
    } else if (focusedField === "defaultCurrencyRate") {
      setFormData((prev) => ({ ...prev, defaultCurrencyRate: cleanValue }));
      // Always mark as manually edited when user enters a value via calculator
      // This ensures the entered value is used directly without multiplication
      if (cleanValue && cleanValue.trim() !== "" && cleanValue.trim() !== "0") {
        setDefaultCurrencyRateManuallyEdited(true);
      }
      // Close calculator immediately so default currency rate field doesn't stay "selected" when category opens
      setFocusedField(null);
    } else if (focusedField === "amount") {
      // Format amount based on currency decimal places
      // If value is "0", format it as "0" (not empty)
      let formattedAmount = value === "0" ? "0" : cleanValue;
      if (formattedAmount && formattedAmount.trim() !== "" && formattedAmount !== "0") {
        if (transactionType === "transfer") {
          // Get currency from fromAccount
          const fromAccount = transferFormData.fromAccountId 
            ? accounts.find((acc) => acc.id === transferFormData.fromAccountId)
            : null;
          const transferCurrency = fromAccount?.currency || defaultCurrency;
          formattedAmount = formatAmountToCurrencyDecimalPlaces(
            cleanValue,
            transferCurrency,
            currencies,
            defaultCurrency
          );
        } else {
          // Get currency from formData or account
          const currentCurrency = formData.currency || 
            (formData.accountId ? accounts.find((acc) => acc.id === formData.accountId)?.currency : defaultCurrency) || 
            defaultCurrency;
          formattedAmount = formatAmountToCurrencyDecimalPlaces(
            cleanValue,
            currentCurrency,
            currencies,
            defaultCurrency
          );
        }
      }
      
      // Save amount
      if (transactionType === "transfer") {
        setTransferFormData((prev) => ({ ...prev, amount: formattedAmount }));
        // For transfers, advance to fromAccount after saving
        setTimeout(() => {
          handleFieldFocus("fromAccount");
        }, 200);
      } else {
        // For non-transfer transactions, check account and category state before updating
        const currentAccount = formData.accountId;
        const hasAccount = currentAccount && currentAccount.trim() !== "";
        const currentCategory = formData.category;
        const hasCategory = currentCategory && currentCategory.trim() !== "";
        
        // Update form data
        setFormData((prev) => ({ ...prev, amount: formattedAmount }));
        
        // After state update, navigate based on account and category selection
        setTimeout(() => {
          if (formattedAmount && formattedAmount !== "") {
            // Amount is filled (including "0") - check account first
            if (!hasAccount) {
              // Account is not selected, go to account field
              handleFieldFocus("account");
            } else if (!hasCategory) {
              // Account is selected but category is not, go to category
              handleFieldFocus("category");
            } else {
              // Both account and category are selected, just close amount field
              setFocusedField(null);
            }
          } else {
            // Amount is empty, just close the amount field
            setFocusedField(null);
          }
        }, 200);
      }
    } else if (focusedField === "feeAmount") {
      // Format fee amount based on fee account currency decimal places
      // If value is "0", format it as "0" (not empty)
      let formattedFeeAmount = value === "0" ? "0" : cleanValue;
      const feeAccountId = isFeeEditMode && feeEditFormData?.feeAccountId
        ? feeEditFormData.feeAccountId
        : transactionType === "transfer"
          ? transferFormData.feeAccountId
          : (formData as { feeAccountId?: string }).feeAccountId;
      if (formattedFeeAmount && formattedFeeAmount.trim() !== "" && formattedFeeAmount !== "0" && feeAccountId) {
        const feeAccount = accounts.find((acc) => acc.id === feeAccountId);
        const feeCurrency = feeAccount?.currency || defaultCurrency;
        formattedFeeAmount = formatAmountToCurrencyDecimalPlaces(
          cleanValue,
          feeCurrency,
          currencies,
          defaultCurrency
        );
      }

      if (isFeeEditMode && setFeeEditFormData) {
        setFeeEditFormData((prev) => ({ ...prev, feeAmount: formattedFeeAmount }));
      } else if (transactionType === "transfer") {
        setTransferFormData((prev) => ({ ...prev, feeAmount: formattedFeeAmount }));
      } else {
        setFormData((prev) => ({ ...prev, feeAmount: formattedFeeAmount }));
      }

      // After fee amount, move to fee account
      setTimeout(() => {
        handleFieldFocus("feeAccount");
      }, 200);
    }
  }, [
    transactionType,
    focusedField,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    setFocusedField,
    setCalculatorExpression,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    hasEvaluatedRef,
    handleFieldFocus,
    currencies,
    defaultCurrency,
    accounts,
    isFeeEditMode,
    feeEditFormData?.feeAccountId,
    setFeeEditFormData,
  ]);

  return {
    handleCalculatorCheck,
  };
}
